import { MutableRefObject, RefObject, useLayoutEffect, useRef } from 'react'

const EXPAND_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
const EXPAND_DURATION_MS = 450
const MEDIA_PREFERS_REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

/**
 * Animates a container div from a captured start height to its natural scroll height
 * when `isExpanded` transitions from false → true.
 *
 * Two modes:
 *
 * **Self-measuring** (omit `startHeightRef`): the hook reads `offsetHeight` from the
 * attached element whenever `isExpanded` is `false`. Use this when the element is
 * always mounted in both collapsed and expanded states (e.g. a row that gains more
 * items). The start height is always the element's actual rendered height — never 0.
 *
 * **External capture** (provide `startHeightRef`): caller is responsible for writing
 * the desired start height into `startHeightRef.current` immediately before setting
 * `isExpanded` to `true`. Use this when the animated element only exists in the
 * expanded state (e.g. a panel that replaces a trigger button).
 */
export function useSlideDownAnimation(
  isExpanded: boolean,
  startHeightRef?: MutableRefObject<number>,
): RefObject<HTMLDivElement> {
  const innerRef = useRef<HTMLDivElement>(null)
  const internalHeightRef = useRef(0)

  const resolvedStartHeightRef = startHeightRef ?? internalHeightRef

  useLayoutEffect(() => {
    const inner = innerRef.current
    if (!inner) return

    // DOM/CSS keywords — not user-facing copy (i18next rule false positives below).
    /* eslint-disable i18next/no-literal-string */
    if (!isExpanded) {
      // Self-measuring mode: snapshot the element's current height while collapsed so
      // it is ready as the animation start point when the element next expands.
      resolvedStartHeightRef.current = inner.offsetHeight
      return
    }

    if (window.matchMedia(MEDIA_PREFERS_REDUCED_MOTION).matches) {
      inner.style.maxHeight = ''
      inner.style.overflow = ''
      return
    }

    const startPx = resolvedStartHeightRef.current
    const targetPx = Math.max(inner.scrollHeight, startPx)

    inner.style.overflow = 'hidden'
    inner.style.transition = 'none'
    inner.style.maxHeight = `${startPx}px`

    void inner.offsetHeight

    inner.style.transition = `max-height ${EXPAND_DURATION_MS}ms ${EXPAND_EASING}`

    requestAnimationFrame(() => {
      inner.style.maxHeight = `${targetPx}px`
    })

    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName !== 'max-height') return
      inner.style.maxHeight = ''
      inner.style.overflow = ''
      inner.style.transition = ''
      inner.removeEventListener('transitionend', onTransitionEnd)
    }

    inner.addEventListener('transitionend', onTransitionEnd)

    return () => {
      inner.removeEventListener('transitionend', onTransitionEnd)
    }
    /* eslint-enable i18next/no-literal-string */
  }, [isExpanded])

  return innerRef
}
